import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type Colour, getEndpoint } from '@tyro/api';
import {
  ColorPicker,
  CopyClipboardButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LoadingPlaceholderContainer,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import { useState } from 'react';
import { useEmbeddableCalendarConfig } from '../../../../api/embedable-cal';

interface EmbeddableCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function EmbeddableCalendarModal({
  isOpen,
  onClose,
}: EmbeddableCalendarModalProps) {
  const { t } = useTranslation(['common', 'calendar']);
  const [theme, setTheme] = useState<Colour | null>(null);
  const { data: embeddableCalendarConfig, isLoading } =
    useEmbeddableCalendarConfig({
      enabled: isOpen,
    });

  const iCalLink = getEndpoint().replace(
    '/tyrogql',
    `/ical?cal_id=${embeddableCalendarConfig?.externalCalendarId}`,
  );
  const themeParam = theme ? `&theme=${theme}` : '';
  const iFrameLink = `<iframe src="${embeddableCalendarConfig?.externalCalendarLink}?cal_id=${embeddableCalendarConfig?.externalCalendarId}&${themeParam}" width="100%" height="616" frameborder="0" style="border: 0; display: block; margin: 1rem auto; transform: translateX(-8px); width: calc(100% + 16px)"></iframe>`;

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle onClose={onClose}>Embeddable Calendar</DialogTitle>
      <DialogContent>
        <LoadingPlaceholderContainer isLoading={isLoading}>
          <Stack spacing={4}>
            <DialogContentText>
              {t('calendar:embeddableCalendarDescriptionLine1')}
            </DialogContentText>
            <Stack spacing={2}>
              <Stack>
                <Typography variant="subtitle1">iCal</Typography>
                <DialogContentText>
                  {t('calendar:iCalLinkDescription')}
                </DialogContentText>
              </Stack>
              <Box sx={{ display: 'flex', position: 'relative' }}>
                <CopyClipboardButton
                  textToCopy={iCalLink}
                  buttonLabel={t('common:actions.copy')}
                  showIconOnHover={false}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 1,
                  }}
                />
                <TextField
                  label={t('calendar:iCalLink')}
                  value={iCalLink}
                  variant="filled"
                  fullWidth
                  multiline
                  rows={2}
                  sx={{
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      pr: 8,
                    },
                  }}
                />
              </Box>
            </Stack>

            <Stack spacing={2}>
              <Stack>
                <Typography variant="subtitle1">
                  {t('calendar:iFrame')}
                </Typography>
                <DialogContentText>
                  {t('calendar:iFrameCodeDescription')}
                </DialogContentText>
              </Stack>
              <ColorPicker
                label={t('calendar:theme')}
                value={theme}
                onChange={(_, value) => setTheme(value as Colour | null)}
              />

              <Box sx={{ display: 'flex', position: 'relative' }}>
                <CopyClipboardButton
                  textToCopy={iFrameLink}
                  buttonLabel={t('common:actions.copy')}
                  showIconOnHover={false}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    zIndex: 1,
                  }}
                />
                <TextField
                  label={t('calendar:iFrameCode')}
                  value={iFrameLink}
                  variant="filled"
                  fullWidth
                  multiline
                  rows={4}
                  sx={{
                    '& textarea': {
                      fontFamily: 'monospace',
                      fontSize: '0.8rem',
                      pr: 8,
                    },
                  }}
                />
              </Box>
            </Stack>
          </Stack>
        </LoadingPlaceholderContainer>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" variant="soft" onClick={onClose}>
          {t('common:actions.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
